import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, fmtPrice, resolveImage } from "@/lib/api";
import { Bed, Bathtub, ArrowsOutSimple, MapPin, House, CheckCircle, FilePdf, VideoCamera, Cube, CaretDown, Train } from "@phosphor-icons/react";
import { toast } from "sonner";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import LeadForm from "@/components/LeadForm";
import WhatsAppButton from "@/components/WhatsAppButton";
import { PropertyMap } from "@/components/PropertyMap";
import { nearestMetro, metroBadgeAccent } from "@/lib/metroWalk";
import FavoriteButton from "@/components/FavoriteButton";
import CompareCheckbox from "@/components/CompareCheckbox";
import { recentlyViewed } from "@/lib/storage";
import { listingSchema, breadcrumbSchema, faqSchema } from "@/lib/schema";

export default function PropertyDetail() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [agent, setAgent] = useState(null);
  const [idx, setIdx] = useState(0);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/listings/${id}`);
      setP(data);
      recentlyViewed.push(data.id);
      if (data.agent_id) {
        try { const a = await api.get(`/agents/${data.agent_id}`); setAgent(a.data); } catch {}
      }
    })();
  }, [id]);

  if (!p) return <div className="min-h-[60vh] flex items-center justify-center text-neutral-500">Loading…</div>;

  const faqs = p.faqs?.length ? p.faqs : defaultFaqs(p);
  const crumbs = [
    { name: "Home", path: "/" },
    { name: p.listing_type === "rent" ? "Rent" : p.listing_type === "off-plan" ? "Off-plan" : p.listing_type === "commercial" ? "Commercial" : "Buy", path: p.listing_type === "rent" ? "/rent" : p.listing_type === "off-plan" ? "/off-plan" : p.listing_type === "commercial" ? "/commercial" : "/properties" },
    { name: p.community, path: `/areas/${slugify(p.community)}` },
    { name: p.title, path: `/properties/${p.id}` },
  ];

  const shareImage = p.images?.[0] ? resolveImage(p.images[0]) : undefined;

  return (
    <div data-testid="property-detail">
      <SEO
        title={`${p.title} — ${fmtPrice(p.price, p.currency)}`}
        description={p.description?.slice(0, 160)}
        path={`/properties/${p.id}`}
        image={shareImage}
        type="article"
        schema={[listingSchema(p), breadcrumbSchema(crumbs), faqSchema(faqs.map((f) => ({ q: f.q, a: f.a })))]}
      />

      <section className="pt-6">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12">
          <Breadcrumbs items={crumbs} />
          {/* Gallery */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
            <div className="lg:col-span-2 aspect-[16/10] bg-[#ffffff] pp-hover-zoom relative">
              {p.images?.[idx] && <img src={resolveImage(p.images[idx])} alt={p.title} className="w-full h-full object-cover" />}
              <div className="absolute top-4 right-4 flex gap-2">
                <FavoriteButton id={p.id} />
                <CompareCheckbox id={p.id} />
              </div>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-1 gap-3">
              {(p.images || []).slice(0, 3).map((im, i) => (
                <button key={i} onClick={() => setIdx(i)} className={`aspect-[4/3] pp-hover-zoom relative ${idx === i ? "outline outline-2 outline-[#001588]" : ""}`} data-testid={`gallery-thumb-${i}`}>
                  <img src={resolveImage(im)} alt={`${p.title || "Property"} – gallery image ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1440px] mx-auto px-6 lg:px-12 grid lg:grid-cols-3 gap-12">
          <div className="lg:col-span-2">
            <div className="overline text-[#001588] mb-3 flex items-center gap-2">
              <MapPin size={12} weight="fill" /> {p.community}{p.sub_community ? ` · ${p.sub_community}` : ""} · Ref {p.reference}
            </div>
            <h1 className="font-serif text-4xl lg:text-5xl leading-tight mb-4">{p.title}</h1>
            <div className="flex items-baseline gap-3 mb-6">
              <span className="font-serif text-4xl text-[#001588]">{fmtPrice(p.price, p.currency)}</span>
              {p.price_period && <span className="text-neutral-500">/ {p.price_period}</span>}
            </div>

            {(p.video_url || p.tour_url || p.brochure_url) && (
              <div className="flex flex-wrap gap-2 mb-8">
                {p.video_url && <MediaBtn href={p.video_url} icon={VideoCamera} label="Watch video" testId="btn-video" />}
                {p.tour_url && <MediaBtn href={p.tour_url} icon={Cube} label="360° tour" testId="btn-tour" />}
                {p.brochure_url && <MediaBtn href={p.brochure_url} icon={FilePdf} label="Brochure PDF" testId="btn-brochure" />}
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 border-t border-b border-neutral-200 py-6 mb-10">
              <Meta icon={Bed} label="Bedrooms" value={p.bedrooms} />
              <Meta icon={Bathtub} label="Bathrooms" value={p.bathrooms} />
              <Meta icon={ArrowsOutSimple} label="Area (sqft)" value={Number(p.area_sqft).toLocaleString()} />
              <Meta icon={House} label="Type" value={p.property_type} />
            </div>

            <h2 className="font-serif text-3xl mb-4">Overview</h2>
            <p className="text-neutral-700 leading-relaxed whitespace-pre-line mb-10">{p.description}</p>

            {p.amenities?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Amenities</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-10">
                  {p.amenities.map((a) => (
                    <div key={a} className="flex items-center gap-2 text-sm text-neutral-700">
                      <CheckCircle size={16} weight="fill" className="text-[#001588]" /> {a}
                    </div>
                  ))}
                </div>
              </>
            )}

            {p.features?.length > 0 && (
              <>
                <h3 className="font-serif text-2xl mb-4">Features</h3>
                <div className="flex flex-wrap gap-2 mb-10">
                  {p.features.map((f) => (
                    <span key={f} className="text-xs tracking-wider uppercase border border-neutral-300 px-3 py-1.5">{f}</span>
                  ))}
                </div>
              </>
            )}

            {(p.developer || p.completion_date || p.payment_plan) && (
              <div className="border border-neutral-200 p-6 mb-10">
                <div className="overline text-[#001588] mb-4">Developer & Delivery</div>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  {p.developer && <div><div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Developer</div>{p.developer_name || p.developer}</div>}
                  {p.completion_date && <div><div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Handover</div>{p.completion_date}</div>}
                  {p.payment_plan && <div><div className="text-neutral-500 text-xs uppercase tracking-wider mb-1">Payment plan</div>{p.payment_plan}</div>}
                </div>
                {(p.project_slug || p.developer_slug) && (
                  <div className="mt-4 pt-4 border-t border-neutral-200 flex flex-wrap gap-4 text-sm">
                    {p.project_slug && <Link to={`/projects/${p.project_slug}`} className="text-[#001588] hover:underline" data-testid="pd-project-link">View project · {p.project_name} →</Link>}
                    {p.developer_slug && <Link to={`/developers/${p.developer_slug}`} className="text-neutral-600 hover:text-[#001588]" data-testid="pd-developer-link">More by {p.developer_name || p.developer} →</Link>}
                  </div>
                )}
              </div>
            )}

            {/* Map */}
            <h3 className="font-serif text-2xl mb-4">Location</h3>
            <PropertyMap lat={p.latitude} lng={p.longitude} community={p.community} title={p.title} />
            <div className="text-xs text-neutral-500 mt-3">{p.address || p.community}, Dubai</div>

            {/* Nearest metro — shown only when the property is within walking distance */}
            {(() => {
              const metro = nearestMetro(p.latitude, p.longitude);
              if (!metro) return null;
              const km = (metro.distance_m / 1000).toFixed(1);
              return (
                <div
                  className="mt-6 border border-neutral-200 bg-white p-5 flex items-start gap-4"
                  data-testid="detail-metro-panel"
                >
                  <div
                    className="w-11 h-11 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: metroBadgeAccent(metro.line), color: "#ffffff" }}
                  >
                    <Train size={22} weight="fill" />
                  </div>
                  <div className="flex-1">
                    <div className="overline text-[#001588] mb-1">Nearest metro</div>
                    <div className="font-serif text-xl text-[#050d19]" data-testid="detail-metro-name">
                      {metro.name}
                    </div>
                    <div className="text-sm text-neutral-600 mt-1">
                      Dubai Metro · {metro.line === "red" ? "Red" : "Green"} Line — approximately{" "}
                      <strong className="text-[#050d19]" data-testid="detail-metro-distance">
                        {km} km
                      </strong>{" "}
                      away · a{" "}
                      <strong className="text-[#050d19]" data-testid="detail-metro-walk">
                        {metro.minutes}-minute walk
                      </strong>{" "}
                      at typical pace.
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* FAQs */}
            <h3 className="font-serif text-2xl mb-4 mt-12">Frequently asked</h3>
            <div className="border-t border-neutral-200">
              {faqs.map((f, i) => (
                <button
                  key={i}
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left border-b border-neutral-200 py-5"
                  data-testid={`faq-${i}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <span className="font-serif text-lg text-neutral-900">{f.q}</span>
                    <CaretDown size={16} className={`transition-transform ${openFaq === i ? "rotate-180" : ""} text-[#001588]`} />
                  </div>
                  {openFaq === i && <div className="text-neutral-700 mt-3 leading-relaxed">{f.a}</div>}
                </button>
              ))}
            </div>

            {p.permit_number && <div className="text-xs text-neutral-400 tracking-wider mt-10">RERA permit no. {p.permit_number}</div>}
          </div>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-4">
              {agent && (
                <div className="border border-neutral-200 p-6">
                  <div className="flex items-center gap-4 mb-4">
                    {agent.photo_url && <img src={agent.photo_url} alt={agent.name} className="w-14 h-14 object-cover" />}
                    <div>
                      <div className="font-serif text-xl">{agent.name}</div>
                      <div className="text-xs text-neutral-500">Point Penta advisor</div>
                    </div>
                  </div>
                  {agent.phone && <div className="text-sm text-neutral-700 mb-1">{agent.phone}</div>}
                  <div className="text-sm text-neutral-700 mb-4">{agent.email}</div>
                  <WhatsAppButton
                    phone={agent.phone} className="w-full"
                    message={`Hi ${agent.name.split(" ")[0]}, I'd like to enquire about ${p.reference} — ${p.title}.`}
                    testId={`whatsapp-${p.id}`}
                  />
                </div>
              )}
              <div className="border border-neutral-200 p-6">
                <div className="overline text-[#001588] mb-3">Book a viewing</div>
                <LeadForm
                  source="listing-detail"
                  interest="viewing"
                  listingId={p.id}
                  submitLabel="Request viewing"
                  messagePlaceholder="Preferred day / time, any specifics…"
                  testIdPrefix="lead"
                />
              </div>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function MediaBtn({ href, icon: Icon, label, testId }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" data-testid={testId}
       className="inline-flex items-center gap-2 border border-neutral-300 px-4 py-2 text-sm hover:border-[#001588] hover:text-[#001588] transition-colors">
      <Icon size={14} /> {label}
    </a>
  );
}
function Meta({ icon: Icon, label, value }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-neutral-500 mb-1"><Icon size={14} /> {label}</div>
      <div className="text-lg text-neutral-900 font-serif capitalize">{value}</div>
    </div>
  );
}
function slugify(s) { return (s || "").toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""); }
function defaultFaqs(p) {
  return [
    { q: `What is the RERA permit number for ${p.title}?`, a: `The RERA permit for this mandate is ${p.permit_number || "available on request"}. All Point Penta listings are compliant with Dubai Land Department regulations.` },
    { q: `Is ${p.title} eligible for a Golden Visa?`, a: p.price >= 2000000 ? "Yes — this property meets the AED 2M threshold for the 10-year UAE Golden Visa. Ask your advisor for the full application process." : "This property is below the AED 2M Golden Visa threshold, but our team can advise on Golden Visa-eligible alternatives." },
    { q: `What is the expected rental yield in ${p.community}?`, a: `Current average yields in ${p.community} sit between 5.5% and 7.5% depending on size and view. Speak to your Point Penta advisor for a comparable market analysis.` },
    { q: "Can I arrange a private viewing?", a: "Yes. Use the enquiry form or WhatsApp your Point Penta advisor. Most viewings are arranged within 24 hours, including weekends." },
  ];
}
